import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { createOrganization, deleteOrganization, setOrganizationVerified } from '../services/ApiService';
import { isValidSwedishOrganizationNumber } from '../utils/swedishValidation';
import { Building2, Plus, ArrowRight, LogOut, Compass, Trash2 } from 'lucide-react';
import Modal from '../components/Modal';
import { AnalyticsEvents, track } from '../services/AnalyticsService';
import type { DemoLocationState } from '../demo/DemoSession';
import type { Organization } from '../types/models';

const demoState = (user: { fullName: string; email: string }): DemoLocationState =>
  ({ demoViewer: { fullName: user.fullName, email: user.email } });

export default function OrganizationSelector() {
  const { user, signOut } = useAuth();
  const { organizations, setCurrentOrgId, refreshOrganizations } = useOrganization();
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [orgDesc, setOrgDesc] = useState('');
  const [orgNumber, setOrgNumber] = useState('');
  const [orgNumberError, setOrgNumberError] = useState<string | null>(null);
  const [noOrgNumberYet, setNoOrgNumberYet] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [orgToDelete, setOrgToDelete] = useState<Organization | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [verifyingOrgId, setVerifyingOrgId] = useState<string | null>(null);

  const handleSelectOrg = (orgId: string) => {
    setCurrentOrgId(orgId);
    track(AnalyticsEvents.OrganizationSelected, { orgId, source: 'selector' });
    navigate(`/org/${orgId}/feed`);
  };

  const validateOrgNumber = (value: string): boolean => {
    if (noOrgNumberYet) {
      setOrgNumberError(null);
      return true;
    }
    if (!isValidSwedishOrganizationNumber(value)) {
      setOrgNumberError('Ogiltigt organisationsnummer');
      return false;
    }
    setOrgNumberError(null);
    return true;
  };

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setError('');
    if (!validateOrgNumber(orgNumber)) return;
    setCreating(true);

    try {
      const org = await createOrganization({
        name: orgName,
        description: orgDesc,
        orgNumber: noOrgNumberYet ? null : orgNumber,
      });
      track(AnalyticsEvents.OrganizationCreated, { orgId: org.id, hasOrgNumber: !noOrgNumberYet });
      await refreshOrganizations();
      setShowCreate(false);
      setOrgName('');
      setOrgDesc('');
      setOrgNumber('');
      setNoOrgNumberYet(false);
      setCreating(false);
      handleSelectOrg(org.id);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Kunde inte skapa föreningen');
      setCreating(false);
    }
  };

  const handleToggleVerified = async (org: Organization) => {
    setVerifyingOrgId(org.id);
    try {
      await setOrganizationVerified(org.id, !org.isVerified);
      await refreshOrganizations();
    } catch {
      alert('Kunde inte ändra verifieringen.');
    } finally {
      setVerifyingOrgId(null);
    }
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setOrgToDelete(null);
    setDeleteConfirmation('');
    setDeleteError('');
  };

  const handleDeleteOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgToDelete) return;
    setDeleteError('');
    setDeleting(true);
    try {
      await deleteOrganization(orgToDelete.id);
      await refreshOrganizations();
      setOrgToDelete(null);
      setDeleteConfirmation('');
    } catch (err: unknown) {
      setDeleteError(err instanceof Error ? err.message : 'Kunde inte radera föreningen');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-teal-50">
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dina föreningar</h1>
            <p className="text-gray-500 mt-1">Välj en förening att hantera</p>
          </div>
          <button
            onClick={signOut}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors text-sm"
          >
            <LogOut className="w-4 h-4" />
            Logga ut
          </button>
        </div>

        <div className="space-y-3">
          {organizations.map(org => (
            <div key={org.id} className="flex items-center gap-2">
              <button
                onClick={() => handleSelectOrg(org.id)}
                className="w-full bg-white rounded-xl border border-gray-200 p-5 flex items-center justify-between hover:border-teal-300 hover:shadow-md transition-all group text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-teal-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{org.name}</h3>
                    {org.description && <p className="text-sm text-gray-500 mt-0.5">{org.description}</p>}
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-teal-600 transition-colors" />
              </button>
              {user?.isSuperAdmin && (
                <label
                  className="flex items-center gap-1.5 text-xs text-gray-600 whitespace-nowrap cursor-pointer"
                  title="Verifierade föreningar har ingen daglig gräns för utskick under provperioden"
                >
                  <input
                    type="checkbox"
                    checked={org.isVerified}
                    disabled={verifyingOrgId === org.id}
                    onChange={() => handleToggleVerified(org)}
                    className="rounded border-gray-300 text-teal-700 focus:ring-teal-500"
                  />
                  Verifierad
                </label>
              )}
              {user?.isSuperAdmin && (
                <button
                  onClick={() => setOrgToDelete(org)}
                  aria-label={`Radera ${org.name}`}
                  title="Radera förening"
                  className="p-3 rounded-xl text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              )}
            </div>
          ))}

          <button
            onClick={() => setShowCreate(true)}
            className="w-full rounded-xl border-2 border-dashed border-gray-200 p-5 flex items-center justify-center gap-2 text-gray-500 hover:border-teal-300 hover:text-teal-700 transition-all"
          >
            <Plus className="w-5 h-5" />
            Skapa ny förening
          </button>

          {user && (
            <Link
              to="/org/demo/feed"
              state={demoState(user)}
              className="flex items-center justify-center gap-2 pt-2 text-sm text-gray-500 hover:text-teal-700 transition-colors"
            >
              <Compass className="w-4 h-4" />
              Utforska testföreningen – se hur allt fungerar utan att något sparas
            </Link>
          )}
        </div>

        <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Skapa ny förening">
          <form onSubmit={handleCreateOrg} className="space-y-4">
            {error && (
              <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl text-sm border border-red-100">{error}</div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Föreningsnamn</label>
              <input
                type="text"
                value={orgName}
                onChange={e => setOrgName(e.target.value)}
                required
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                placeholder="t.ex. Solbackens Samfallighet"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Beskrivning (valfritt)</label>
              <textarea
                value={orgDesc}
                onChange={e => setOrgDesc(e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none"
                placeholder="Beskriv föreningen..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Organisationsnummer</label>
              <input
                type="text"
                value={orgNumber}
                onChange={e => { setOrgNumber(e.target.value); if (orgNumberError) setOrgNumberError(null); }}
                onBlur={e => validateOrgNumber(e.target.value)}
                required={!noOrgNumberYet}
                disabled={noOrgNumberYet}
                placeholder="XXXXXX-XXXX"
                className={`w-full px-4 py-2.5 rounded-xl border focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none disabled:bg-gray-100 disabled:text-gray-400 ${orgNumberError ? 'border-red-300' : 'border-gray-200'}`}
              />
              {orgNumberError && <p className="text-sm text-red-600 mt-1">{orgNumberError}</p>}
              <label className="flex items-center gap-2 mt-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={noOrgNumberYet}
                  onChange={e => { setNoOrgNumberYet(e.target.checked); setOrgNumberError(null); }}
                  className="rounded border-gray-300 text-teal-700 focus:ring-teal-500"
                />
                Vi har ännu inget Organizations nummer
              </label>
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors">
                Avbryt
              </button>
              <button type="submit" disabled={creating} className="px-6 py-2 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium transition-colors disabled:opacity-50">
                {creating ? 'Skapar...' : 'Skapa förening'}
              </button>
            </div>
          </form>
        </Modal>

        <Modal isOpen={orgToDelete !== null} onClose={closeDeleteModal} title="Radera förening" size="sm">
          {orgToDelete && (
            <form onSubmit={handleDeleteOrg} className="space-y-4">
              {deleteError && (
                <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl text-sm border border-red-100">{deleteError}</div>
              )}
              <p className="text-sm text-gray-700">
                <strong>{orgToDelete.name}</strong> och allt som hör till föreningen raderas permanent: medlemskap, möten,
                protokoll, inlägg, förslag, betalningar, ekonomi och dokument. En aktiv prenumeration avslutas.
                Medlemmarnas användarkonton finns kvar. Det går inte att ångra.
              </p>
              <div>
                <label htmlFor="delete-org-confirmation" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Skriv föreningens namn för att bekräfta
                </label>
                <input
                  id="delete-org-confirmation"
                  type="text"
                  value={deleteConfirmation}
                  onChange={e => setDeleteConfirmation(e.target.value)}
                  placeholder={orgToDelete.name}
                  autoComplete="off"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                />
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <button type="button" onClick={closeDeleteModal} disabled={deleting} className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50">
                  Avbryt
                </button>
                <button
                  type="submit"
                  disabled={deleting || deleteConfirmation.trim() !== orgToDelete.name.trim()}
                  className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
                >
                  {deleting ? 'Raderar...' : 'Radera permanent'}
                </button>
              </div>
            </form>
          )}
        </Modal>
      </div>
    </div>
  );
}
